import { Wallet, Warning } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { isLimitError } from 'src/api/requests';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { SegmentedButtons } from 'src/components/SegmentedButtons';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { UpdateEnvironmentDto } from 'src/interfaces/environment.interface';

type SpendingControlsProps = {
  includedMinutes: number;
  overageRate: number;
};

export const SpendingControls = ({
  includedMinutes,
  overageRate,
}: SpendingControlsProps) => {
  const { patch } = useAuthedRequest();
  const { environmentData } = useEnvironment();
  const notification = useNotification();
  const [overageOption, setOverageOption] = useState<'unlimited' | 'limited'>(
    'limited',
  );
  const [monthlyOverageMinutesLimit, setMonthlyOverageMinutesLimit] =
    useState<number>(0);
  const [estimatedCost, setEstimatedCost] = useState<string>();

  useEffect(() => {
    if (!environmentData) return;
    setOverageOption(
      environmentData.limits.maxMinutes ? 'limited' : 'unlimited',
    );
    if (environmentData.limits.maxMinutes) {
      setMonthlyOverageMinutesLimit(environmentData.limits.maxMinutes);
      setEstimatedCost(
        `$${String(environmentData.limits.maxMinutes * overageRate)}`,
      );
    }
  }, [environmentData]);

  const updateOverageLimit = async () => {
    if (!environmentData) return;

    const payload: UpdateEnvironmentDto = {
      name: environmentData.name,
      limits: {
        ...environmentData.limits,
        maxMinutes:
          overageOption === 'limited'
            ? includedMinutes + monthlyOverageMinutesLimit
            : null,
      },
    };

    try {
      await patch(`/environments/${environmentData?.id}`, payload);
      notification.success('Overage settings updated successfully');
    } catch (error) {
      if (!isLimitError(error)) {
        console.error(error);
        notification.error('Failed to update overage settings');
      }
      return;
    }
  };

  return (
    <div className="p-4 border border-neutral-200 rounded-md bg-neutral-50">
      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <Wallet size={32} className="text-primary" />
          <div className="flex flex-col ml-2">
            <h3>Spending Controls for {environmentData?.name}</h3>
            <span className="text-sm text-neutral-400">
              Set limits to manage your monthly overage costs
            </span>
          </div>
        </div>
        <SegmentedButtons
          value={overageOption}
          onValueChange={(value) =>
            setOverageOption(value as 'unlimited' | 'limited')
          }
          buttons={[
            { value: 'limited', label: 'Set overage limit' },
            { value: 'unlimited', label: 'Unlimited overage' },
          ]}
        />
      </div>
      {overageOption === 'limited' && (
        <div className="flex mb-4 space-between">
          <div className="flex flex-col w-full mr-2">
            <Label className="font-title">Monthly overage limit:</Label>
            <Input
              type="number"
              placeholder="Enter minutes"
              value={monthlyOverageMinutesLimit}
              onChange={(e) => {
                setMonthlyOverageMinutesLimit(parseInt(e.target.value));
                if (!isNaN(parseInt(e.target.value))) {
                  setEstimatedCost(
                    `$${(parseInt(e.target.value) * overageRate).toFixed(2)}`,
                  );
                } else {
                  setEstimatedCost('');
                }
              }}
            />
          </div>
          <div className="flex flex-col w-full ml-2">
            <Label className="font-title">
              Estimated overage cost (at ${overageRate}/min):
            </Label>
            <Input
              type="text"
              placeholder="Enter budget"
              value={estimatedCost}
              onChange={(e) => {
                const newValue = e.target.value;
                // Only allow numeric values and decimal point
                if (newValue === '' || /^\$?[0-9]*\.?[0-9]*$/.test(newValue)) {
                  setEstimatedCost(
                    newValue[0] === '$' ? newValue : `$${newValue}`,
                  );
                  const numericValue = newValue.replace('$', '');
                  if (newValue && !isNaN(parseFloat(numericValue))) {
                    setMonthlyOverageMinutesLimit(
                      Math.floor(parseFloat(numericValue) / overageRate),
                    );
                  }
                }
              }}
            />
          </div>
        </div>
      )}
      {overageOption === 'unlimited' && (
        <div className="flex flex-col p-2 bg-success-100 border border-success-200 rounded-md mb-4">
          <span className="font-bold">No limits to your workloads!</span>
          <span className="text-sm text-neutral-400">
            You'll be charged ${overageRate}/minute for all usage beyond your
            plan limits with no monthly cap. 🎉
          </span>
        </div>
      )}
      <div className="flex justify-end">
        <Button
          variant="contained"
          color="primary"
          onClick={updateOverageLimit}
        >
          Update overage settings
        </Button>
      </div>
    </div>
  );
};
